import { buildSearchEngine, getOrganizationEndpoints, SearchEngine } from "@coveo/headless";
import { coveoConfig } from "../global/coveo-components";
import Util from "../global/util/util";
import { CoveoToken, CoveoTokenService } from "./coveo-token.service";
import { PlatformClientOrigin, PlatformRequestOptions, RequestMetadata } from "@coveo/headless/dist/definitions/api/preprocess-request";

export class CoveoSearchEngineService {

	private static _domain: string;

	private static _headlessEngine: SearchEngine;

	private static readonly PAGER_EVENTS = [
		"pagerNext",
		"pagerPrevious",
		"pagerNumber"
	];

	/**
	 * Organization ID used in coveo config
	 */
	private static _orgId: string;

	constructor(domain: string, orgId: string) {
		CoveoSearchEngineService._domain = domain;
		CoveoSearchEngineService._orgId = orgId;
	}

	public static setUpEngine(coveoToken: CoveoToken) {
		this._orgId = (this._orgId ?? coveoToken.orgId) ?? coveoConfig.orgId;
		this._headlessEngine = buildSearchEngine(
			{
				configuration: {
					accessToken: coveoToken.token,
					organizationId: this._orgId,
					organizationEndpoints: getOrganizationEndpoints(this._orgId),
					preprocessRequest: async (request: PlatformRequestOptions, clientOrigin: PlatformClientOrigin, metadata: RequestMetadata) => {
						// get the body for manipulation in JSON
						const body = JSON.parse(request.body.toString());

						// trigger GA metrics for events
						if ('analyticsFetch' === clientOrigin) {
							const eventName = 'onsite search interaction';

							/* Searchbox Metrics */
							// searchbox click - suggested query
							if ('omniboxAnalytics' === body?.actionCause) {
								Util.makeAnalyticsCall(eventName, 'query suggestion click', 'search bar|suggestion click');
							}

							/* Facet Metrics */
							// breadcrumb - clear all filters
							if ('breadcrumbResetAll' === body?.actionCause) {
								const eventAction = 'oss filter header link click';
								const eventLabel = 'clear all filters';
								Util.makeAnalyticsCall(eventName, eventAction, eventLabel);
							}
							// breadcrumb - clear filter
							if ('breadcrumbFacet' === body?.actionCause) {
								const eventAction = 'oss filter header remove filter';
								const facetId = body?.customData?.facetId ?? body?.customData?.categoryFacetId;
								// for hierarchical filter, pass entire filter value path
								const facetValue = body?.customData?.facetValue ?? body?.customData?.categoryFacetPath?.join(',');
								const eventLabel = `${facetId}: ${facetValue}`;
								Util.makeAnalyticsCall(eventName, eventAction, eventLabel);
							}
							// filter panel (standard & hierarchical) - select filter
							if ('facetSelect' === body?.actionCause) {
								const eventAction = 'oss filter interaction';
								const facetId = body?.customData?.facetId;
								const facetValue = body?.customData?.facetValue;
								if (body.facetState.some(f => f.id === facetId && f.value === facetValue)) {
									const eventLabel = `select ${facetId}: ${facetValue}`;
									Util.makeAnalyticsCall(eventName, eventAction, eventLabel);
								}
							}
							// filter panel (standard) - de-select filter
							if ('facetDeselect' === body?.actionCause) {
								const eventAction = 'oss filter interaction';
								const eventLabel = `deselect ${body?.customData?.facetId}: ${body?.customData?.facetValue}`;
								Util.makeAnalyticsCall(eventName, eventAction, eventLabel);
							}
							// filter panel (hierarchical) - de-select filter
							if ('facetClearAll' === body?.actionCause) {
								const eventAction = 'oss filter interaction';
								const eventLabel = `select ${body?.customData?.facetId}: All Categories`;
								Util.makeAnalyticsCall(eventName, eventAction, eventLabel);
							}
						}

						// modify the body
						body['q'] = Util.cleanseSearchTerm(body['q']);

						if (metadata?.method === 'search' && metadata?.origin === 'mainSearch') {
							// fire metrics for pagination
							const pageNum = body?.analytics?.customData?.pagerNumber;
							if (pageNum && this.PAGER_EVENTS.includes(body.analytics.actionCause)) {
								Util.makeAnalyticsCall('onsite search interaction', 'pagination', `page_${pageNum}`);
							}
						}

						// repackage the body
						request.body = JSON.stringify(body);

						return request;
					},
					renewAccessToken: (() => {
						return CoveoTokenService.getCoveoSearchToken(this._domain).then(coveoToken => coveoToken.token);
					}),
					search: {
						preprocessSearchResponseMiddleware: (response) => {
							// get the locale

							const locale = Util.getLocale();
							// get coveo query
							let coveoQuery = this.getSearchEngine().state.query.q;
							// Trim search term
							coveoQuery = coveoQuery ? coveoQuery.trim() : coveoQuery;
							// terms to highlight
							let termsToHighlight = response.body.termsToHighlight;

							let metricsUriText = `keyMatch=${coveoQuery}&tisearch=universal_search`;
							// append filter params to metrics string
							const url = document.location.href;
							if (url.indexOf('#') >= 0) {
								url.split('#')[1].split('&').forEach((param) => {
									if (param.startsWith('f-') || param.startsWith('cf-')) {
										metricsUriText = `${metricsUriText}&${param}`;
									}
								});
							}

							response.body.results.forEach((result) => {
								// E.g., modify the result object
								if (result.title && Util.SEARCH_CONFIG['removeFromResultTitle']) {
									Util.SEARCH_CONFIG['removeFromResultTitle'].forEach(function (str) {
										const regex = new RegExp(str);
										result.title = result.title.replace(regex, '')
									})
									result.title = Util.decodeHtml(result.title);
								} else if (!result.title) {
									result.title = result.clickUri ? result.clickUri : result.printableUri
								}

								if (!result.raw.description) {
									result.raw.description = result.excerpt;
								} else {
									result.raw.description = Util.decodeHtml(result.raw.description);
								}
								let description = (result.raw.description as any);
								result.raw.description = description.length > 500 ? `${description.substring(0, 500)}...` : description;
								if (termsToHighlight !== undefined) {
									let termsToBold = []
									Object.keys(termsToHighlight).forEach(key => {
										termsToBold = termsToBold.concat([key]);
										termsToBold = termsToBold.concat(Object.values(termsToHighlight[key]));
									})
									result.raw.description = Util.boldText(result.raw.description.toString(), termsToBold);
								}

								if (result.clickUri) {
									result.clickUri = Util.rewriteUrlNonprod(result.clickUri, locale);
									result.clickUri = Util.addQueryParams(result.clickUri, metricsUriText);
								}
								// re-write urls to match environment
								if (result.printableUri) {
									result.printableUri = Util.rewriteUrlNonprod(result.printableUri, locale);
								}
								if (result.raw[`partner_link_html_${locale.toLocaleLowerCase().replace('-', '_')}`]) {
									const linkRegex = new RegExp('www.tij.co.jp|www.ti.com.cn|www.ti.com');
									let resultLink = (result.raw[`partner_link_html_${locale.toLocaleLowerCase().replace('-', '_')}`] as any);
									resultLink = resultLink.replace(linkRegex, () => window.location.hostname);
									result.raw[`partner_link_html_${locale.toLocaleLowerCase().replace('-', '_')}`] = resultLink.replace('<a href',
										'<style>a{color:#007c8c;text-decoration:none;}a:hover{color:#007c8c;text-decoration:underline;}</style> <a href')
								}
							});
							return response;
						},
						searchHub: coveoConfig.searchHub
					}
				}
			});
	}

	public static getSearchEngine(): SearchEngine {
		return this._headlessEngine;
	}

}
